/**
 * ServerScope Component
 * 
 * Fourth step in the AlertForm wizard that allows users to define whether the alert
 * applies globally or to specific servers.
 */

import React, { useState, useEffect } from 'react';
import { useAlertContext } from '../../../contexts/AlertContext';
import { useAuth } from '../../../context/AuthContext';
import { AlertScopeType } from '../../../models/AlertModels';
import QWebToggle from '../../common/QWebToggle';
import '../../common/QWebToggleStyleLoader';

/**
 * ServerScope component for configuring alert scope
 * 
 * @returns {JSX.Element} The ServerScope component
 */
function ServerScope() {
  const { formState, updateFormState } = useAlertContext();
  const { serverData } = useAuth(); // Get server data from authentication context
  const [availableServers, setAvailableServers] = useState([]);
  const [selectedServers, setSelectedServers] = useState([]);
  const [scopeType, setScopeType] = useState(formState.alert.scope?.type || AlertScopeType.GLOBAL);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Function to format server data from auth context into the format we need
  const formatServerData = (servers) => {
    if (!servers || !Array.isArray(servers) || servers.length === 0) {
      console.warn('No server data available from authentication context');
      return [];
    }
    
    console.log('Raw server data from auth context:', servers);
    
    // Map the server data to our expected format
    // Handle both camelCase and snake_case property names
    return servers.map(server => ({
      id: server.server_id || server.serverId || server.id || `server-${Math.random().toString(36).substring(2, 9)}`,
      name: server.server_name || server.serverName || server.name || 'Unknown Server',
      status: server.status || 'online' // Default to online if status not provided
    }));
  };
  
  // Initialize component state from form state and auth context
  useEffect(() => {
    // Format and set available servers from auth context
    const formattedServers = formatServerData(serverData);
    setAvailableServers(formattedServers);
    
    // Initialize selected servers from form state
    if (formState.alert.scope) {
      setScopeType(formState.alert.scope.type);
      
      if (formState.alert.scope.type === AlertScopeType.SERVER_SPECIFIC && 
          formState.alert.scope.serverIds) {
        setSelectedServers(formState.alert.scope.serverIds);
      }
    }
  }, [formState.alert.scope, serverData]); // Add serverData as a dependency
  
  /**
   * Handle scope type change
   * 
   * @param {string} type - The scope type
   */
  const handleScopeTypeChange = (type) => {
    setScopeType(type);
    
    // Update form state
    const updatedScope = {
      type,
      serverIds: type === AlertScopeType.SERVER_SPECIFIC ? selectedServers : []
    };
    
    updateFormState({
      alert: {
        ...formState.alert,
        scope: updatedScope
      }
    });
  };
  
  /**
   * Handle server selection
   * 
   * @param {string} serverId - The server ID
   */
  const handleServerSelect = (serverId) => {
    let updatedSelectedServers;
    
    if (selectedServers.includes(serverId)) {
      // Remove server if already selected
      updatedSelectedServers = selectedServers.filter(id => id !== serverId);
    } else {
      // Add server if not selected
      updatedSelectedServers = [...selectedServers, serverId];
    }
    
    setSelectedServers(updatedSelectedServers);
    
    // Update form state
    updateFormState({
      alert: {
        ...formState.alert,
        scope: {
          type: scopeType,
          serverIds: updatedSelectedServers
        }
      }
    });
  };
  
  /**
   * Filter servers based on search term
   * 
   * @returns {Object[]} Filtered servers
   */
  const getFilteredServers = () => {
    if (!searchTerm) return availableServers;
    
    return availableServers.filter(server => 
      server.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };
  
  return (
    <div className="server-scope-container">
      <h3 className="step-title">Define Alert Scope</h3>
      <p className="step-description">
        Choose whether this alert applies to all servers (global) or only specific servers.
      </p>
      
      <div className="scope-type-selection">
        <div 
          className={`scope-type-option ${scopeType === AlertScopeType.GLOBAL ? 'selected' : ''}`}
          onClick={() => handleScopeTypeChange(AlertScopeType.GLOBAL)}
        >
          <div className="scope-type-icon">
            <i className="fa fa-globe"></i>
          </div>
          <div className="scope-type-content">
            <h4 className="scope-type-title">Global</h4>
            <p className="scope-type-description">
              This alert will apply to all servers in your environment.
            </p>
          </div>
          <div className="scope-type-radio">
            <input 
              type="radio" 
              checked={scopeType === AlertScopeType.GLOBAL}
              onChange={() => {}} // Handled by onClick on parent div
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
        
        <div 
          className={`scope-type-option ${scopeType === AlertScopeType.SERVER_SPECIFIC ? 'selected' : ''}`}
          onClick={() => handleScopeTypeChange(AlertScopeType.SERVER_SPECIFIC)}
        >
          <div className="scope-type-icon">
            <i className="fa fa-server"></i>
          </div>
          <div className="scope-type-content">
            <h4 className="scope-type-title">Server-Specific</h4>
            <p className="scope-type-description">
              This alert will only apply to servers you select below.
            </p>
          </div>
          <div className="scope-type-radio">
            <input 
              type="radio" 
              checked={scopeType === AlertScopeType.SERVER_SPECIFIC}
              onChange={() => {}} // Handled by onClick on parent div
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      </div>
      
      {scopeType === AlertScopeType.SERVER_SPECIFIC && (
        <div className="server-selection">
          <h4 className="server-selection-title">Select Servers</h4>
          
          <div className="server-search">
            <input
              type="text"
              placeholder="Search servers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="server-search-input"
            />
          </div>
          
          <div className="server-list">
            {getFilteredServers().length === 0 ? (
              <div className="no-servers-message">No servers found matching your search.</div>
            ) : (
              getFilteredServers().map((server) => (
                <div 
                  key={server.id}
                  className={`server-item ${selectedServers.includes(server.id) ? 'selected' : ''} ${server.status === 'offline' ? 'offline' : ''}`}
                  onClick={() => handleServerSelect(server.id)}
                >
                  <div className="server-icon">
                    <i className="fa fa-server"></i>
                  </div>
                  <div className="server-details">
                    <div className="server-name">{server.name}</div>
                    <div className={`server-status ${server.status}`}>
                      {server.status === 'online' ? 'Online' : 'Offline'}
                    </div>
                  </div>
                  <div className="server-checkbox">
                    <QWebToggle
                      checked={selectedServers.includes(server.id)}
                      onChange={() => handleServerSelect(server.id)}
                      onClick={(e) => e.stopPropagation()}
                      showLabel={false}
                      className="server-toggle"
                      aria-label={`Select server ${server.name}`}
                      disabled={server.status === 'offline'}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
          
          {scopeType === AlertScopeType.SERVER_SPECIFIC && selectedServers.length === 0 && (
            <div className="server-selection-warning">
              <i className="fa fa-exclamation-triangle"></i>
              Please select at least one server to continue.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ServerScope;
